"use client";

// ═══════════════════════════════════════════════════════════════════════════════
// Bug-190 — VersionBanner: avisa quando ha versao nova em prod
// ═══════════════════════════════════════════════════════════════════════════════
//
// Quando useVersionCheck detecta que a versao do servidor != local, mostra
// barra fixa no topo com botao "Atualizar agora" + auto-reload em 30s.
//
// Tambem expoe botao "Atualizar" pra emergency (sempre visivel via prop force).
//
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from "react";
import { useVersionCheck } from "@/lib/hooks/use-version-check";
import { RefreshCw, X } from "lucide-react";

const AUTO_RELOAD_AFTER_MS = 30_000;

export function VersionBanner() {
  const { mismatch, serverVersion, localVersion, forceReload } = useVersionCheck();
  const [dismissed, setDismissed] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(AUTO_RELOAD_AFTER_MS / 1000);

  // Reset contador quando mismatch muda
  useEffect(() => {
    if (mismatch && !dismissed) {
      setSecondsLeft(AUTO_RELOAD_AFTER_MS / 1000);
    }
  }, [mismatch, dismissed]);

  // Countdown auto-reload
  useEffect(() => {
    if (!mismatch || dismissed) return;
    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(interval);
          forceReload();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [mismatch, dismissed, forceReload]);

  if (!mismatch || dismissed) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-blue-600 text-white px-4 py-2 flex items-center justify-center gap-3 shadow-lg text-sm">
      <RefreshCw className="h-4 w-4 animate-spin" />
      <span>
        Nova versão disponível ({localVersion} → <strong>{serverVersion}</strong>) — atualizando em {secondsLeft}s
      </span>
      <button
        onClick={forceReload}
        className="ml-2 px-3 py-1 rounded bg-white/15 hover:bg-white/25 transition-colors text-xs font-semibold"
      >
        Atualizar agora
      </button>
      <button
        onClick={() => setDismissed(true)}
        className="text-white/70 hover:text-white transition-colors"
        title="Adiar (não recomendado)"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
